import { computed, h, shallowRef, watchEffect } from 'vue';

import { useMergedState, useState } from '@components/composable';
import { addItemToArray, removeItemFromArray, warn } from '@components/utils';

import { CdsCheckbox } from '../../CdsCheckbox';
import { CdsFunctionalButton } from '../../CdsButton';
import { CdsList, CdsListItem } from '../../CdsList';
import { CdsMenu } from '../../CdsMenu';
import { CdsRadio } from '../../CdsRadioButton';
import { useMaxLevel } from '../../CdsTree/composable';
import { conductCheck, convertDataToEntities } from '../../CdsTree/utils';
import { INTERNAL_COL_DEFINE } from '../constants';

import type { NTree } from '../../CdsTree/interface';
import type { NTable } from '../interface';
import type { ComponentProps } from '@components/utils';
import type { Ref, ComputedRef, VNode } from 'vue';

export const SELECTION_COLUMN = {} as const;
export const SELECTION_ALL = 'SELECT_ALL' as const;
export const SELECTION_INVERT = 'SELECT_INVERT' as const;
export const SELECTION_NONE = 'SELECT_NONE' as const;
export const EXPAND_COLUMN = {} as const;

const EMPTY_LIST: NTable.TKey[] = [];

export type INTERNAL_SELECTION_ITEM =
  | NTable.NRow.ISelectionItem
  | typeof SELECTION_ALL
  | typeof SELECTION_INVERT
  | typeof SELECTION_NONE;

export interface ISelectionOptions<RecordType> {
  pageData: Ref<RecordType[]>;
  data: Ref<RecordType[]>;
  getRowKey: Ref<NTable.NRow.TGetRowKey<RecordType>>;
  getRecordByKey: (key: NTable.TKey) => RecordType;
  expandType: Ref<NTable.NExpand.TExpandableType>;
  childrenColumnName: Ref<string>;
  locale: Ref<NTable.ILocale>;
  getPopupContainer?: ComputedRef<NTable.NCustomize.TGetPopupContainer | undefined>;
}

export interface IUseSelection<RecordType> {
  transformColumns: NTable.NColumn.TTransformColumns<RecordType>;
  selectedKeySet: Ref<Set<NTable.TKey>>;
}

export function useSelection<RecordType>(
  rowSelectionRef: Ref<NTable.NRow.IRowSelection<RecordType> | undefined>,
  configRef: ISelectionOptions<RecordType>
): IUseSelection<RecordType> {
  const mergedRowSelection = computed(() => {
    const temp = rowSelectionRef.value || {};
    const { checkStrictly = true } = temp;
    return { ...temp, checkStrictly };
  });

  // ========================= Keys =========================
  const [mergedSelectedKeys, setMergedSelectedKeys] = useMergedState(
    mergedRowSelection.value.selectedRowKeys || mergedRowSelection.value.defaultSelectedRowKeys || EMPTY_LIST,
    {
      value: computed(() => mergedRowSelection.value.selectedRowKeys)
    }
  );
  // ========================= Keys =========================

  // ======================== Caches ========================
  const preserveRecordsRef = shallowRef(new Map<NTable.TKey, RecordType>());

  const updatePreserveRecordsCache = (keys: NTable.TKey[]) => {
    if (mergedRowSelection.value.preserveSelectedRowKeys) {
      const newCache = new Map<NTable.TKey, RecordType>();

      // Keep key if mark as preserveSelectedRowKeys
      keys.forEach((key) => {
        let record = configRef.getRecordByKey(key);

        if (!record && preserveRecordsRef.value.has(key)) {
          record = preserveRecordsRef.value.get(key)!;
        }

        newCache.set(key, record);
      });

      // Refresh to new cache
      preserveRecordsRef.value = newCache;
    }
  };

  watchEffect(() => {
    updatePreserveRecordsCache(mergedSelectedKeys.value || EMPTY_LIST);
  });

  const keyEntities = computed(() =>
    mergedRowSelection.value.checkStrictly
      ? null
      : convertDataToEntities(configRef.data.value as unknown as NTree.IDataNode[], {
          externalGetKey: configRef.getRowKey.value as any,
          childrenPropName: configRef.childrenColumnName.value
        }).keyEntities
  );

  // Get flatten data
  const flattedData = computed(() => flattenData(configRef.pageData.value, configRef.childrenColumnName.value));

  // Get all checkbox props
  const checkboxPropsMap = computed(() => {
    const map = new Map<NTable.TKey, Partial<ComponentProps<typeof CdsCheckbox>>>();
    const getRowKey = configRef.getRowKey.value;
    const getCheckboxProps = mergedRowSelection.value.getCheckboxProps;

    flattedData.value.forEach((record, index) => {
      const key = getRowKey(record, index);
      const checkboxProps = (getCheckboxProps ? getCheckboxProps(record) : null) || {};

      map.set(key, checkboxProps);

      if ('checked' in checkboxProps) {
        warn('CdsTable', 'Do not set `checked` in `getCheckboxProps`. Please use `selectedRowKeys` instead.');
      }
    });

    return map;
  });

  const { maxLevel, levelEntities } = useMaxLevel(keyEntities);
  const isCheckboxDisabled: NTree.TGetCheckDisabled<RecordType> = (record: RecordType) =>
    !!checkboxPropsMap.value.get(configRef.getRowKey.value(record))?.disabled;
  const { preserveSelectedRowKeys, onChange: onSelectionChange } = mergedRowSelection.value;
  const { getRecordByKey } = configRef;

  const selectKeysState = computed(() => {
    if (mergedRowSelection.value.checkStrictly) {
      return { checkedKeys: mergedSelectedKeys.value || [], halfCheckedKeys: [] };
    }

    return conductCheck(
      mergedSelectedKeys.value!,
      true,
      keyEntities.value,
      maxLevel.value,
      levelEntities.value!,
      isCheckboxDisabled as any,
      preserveSelectedRowKeys
    );
  });

  const resultSelectedKeys = computed(() => selectKeysState.value.checkedKeys);
  const resultHalfSelectedKeys = computed(() => selectKeysState.value.halfCheckedKeys);

  const resultSelectedKeySet = computed<Set<NTable.TKey>>(() => {
    const keys =
      mergedRowSelection.value.type === 'radio' ? resultSelectedKeys.value.slice(0, 1) : resultSelectedKeys.value;
    return new Set(keys);
  });
  const resultHalfSelectedKeySet = computed<Set<NTable.TKey>>(() =>
    mergedRowSelection.value.type === 'radio' ? new Set() : new Set(resultHalfSelectedKeys.value)
  );

  // Save last selected key to enable range selection
  const [lastSelectedKey, setLastSelectedKey] = useState<NTable.TKey | null>(null);

  const setSelectedKeys = (keys: NTable.TKey[]) => {
    let availableKeys: NTable.TKey[];
    let records: RecordType[];

    updatePreserveRecordsCache(keys);

    if (preserveSelectedRowKeys) {
      availableKeys = keys;
      records = keys.map((key) => preserveRecordsRef.value.get(key)!);
    } else {
      // Filter key which not exist in the `data`
      availableKeys = [];
      records = [];

      keys.forEach((key) => {
        const record = getRecordByKey(key);

        if (record !== undefined) {
          availableKeys.push(key);
          records.push(record);
        }
      });
    }

    setMergedSelectedKeys(availableKeys);

    onSelectionChange?.(availableKeys, records);
  };
  // ======================== Caches ========================

  // ====================== Selections ======================
  const triggerSingleSelection = (key: NTable.TKey, selected: boolean, keys: NTable.TKey[], event: Event) => {
    const { onSelect } = mergedRowSelection.value;
    const { getRecordByKey } = configRef || {};

    if (onSelect) {
      const rows = keys.map((k) => getRecordByKey(k));
      onSelect(getRecordByKey(key), selected, rows, event);
    }

    setSelectedKeys(keys);
  };

  const mergedSelections = computed(() => {
    const { onSelectNone, selections, hideSelectAll } = mergedRowSelection.value;
    const { data, pageData, getRowKey, locale: tableLocale } = configRef;

    if (!selections || hideSelectAll) return null;

    const selectionList: INTERNAL_SELECTION_ITEM[] =
      selections === true ? [SELECTION_ALL, SELECTION_INVERT, SELECTION_NONE] : selections;

    return selectionList.map((selection: INTERNAL_SELECTION_ITEM) => {
      if (selection === SELECTION_ALL) {
        return {
          key: 'all',
          text: tableLocale.value.selectionAll,
          onSelect() {
            setSelectedKeys(
              data.value
                .map((record, index) => getRowKey.value(record, index))
                .filter((key) => {
                  const checkProps = checkboxPropsMap.value.get(key);
                  return !checkProps?.disabled || resultSelectedKeySet.value.has(key);
                })
            );
          }
        };
      }

      if (selection === SELECTION_INVERT) {
        return {
          key: 'invert',
          text: tableLocale.value.selectInvert,
          onSelect() {
            const keySet = new Set(resultSelectedKeySet.value);

            pageData.value.forEach((record, index) => {
              const key = getRowKey.value(record, index);
              const checkProps = checkboxPropsMap.value.get(key);

              if (!checkProps?.disabled) {
                if (keySet.has(key)) {
                  keySet.delete(key);
                } else {
                  keySet.add(key);
                }
              }
            });

            const keys = Array.from(keySet);

            setSelectedKeys(keys);
          }
        };
      }

      if (selection === SELECTION_NONE) {
        return {
          key: 'none',
          text: tableLocale.value.selectNone,
          onSelect() {
            onSelectNone?.();
            setSelectedKeys(
              Array.from(resultSelectedKeySet.value).filter((key) => {
                const checkProps = checkboxPropsMap.value.get(key);
                return checkProps?.disabled;
              })
            );
          }
        };
      }

      return selection as NTable.NRow.ISelectionItem;
    });
  });

  const flattedDataLength = computed(() => flattedData.value.length);
  // ====================== Selections ======================

  // ====================== Columns ======================
  const transformColumns = (columns: NTable.NColumn.TColumns<RecordType>): NTable.NColumn.TColumns<RecordType> => {
    const {
      onSelectMultiple,
      columnWidth: selectionColWidth,
      type: selectionType,
      fixed,
      renderCell: customizeRenderCell,
      hideSelectAll,
      checkStrictly
    } = mergedRowSelection.value;

    const { getRecordByKey, getRowKey, expandType, getPopupContainer } = configRef;

    if (!rowSelectionRef.value) {
      if (columns.includes(SELECTION_COLUMN)) {
        warn('CdsTable', '`rowSelection` is not config but `SELECTION_COLUMN` exists in the `columns`.');
      }

      return columns.filter((col) => col !== SELECTION_COLUMN);
    }

    // Support selection
    let cloneColumns = columns.slice();
    const keySet = new Set(resultSelectedKeySet.value);

    // Record key only need check with enabled
    const recordKeys = flattedData.value
      .map(getRowKey.value)
      .filter((key) => !checkboxPropsMap.value.get(key)!.disabled);
    const checkedCurrentAll = recordKeys.every((key) => keySet.has(key));
    const checkedCurrentSome = recordKeys.some((key) => keySet.has(key));

    const onSelectAllChange = () => {
      if (checkedCurrentAll) {
        recordKeys.forEach((key) => {
          keySet.delete(key);
        });
      } else {
        recordKeys.forEach((key) => {
          keySet.add(key);
        });
      }

      const keys = Array.from(keySet);

      setSelectedKeys(keys);
    };

    // ===================== Render =====================
    // Title Cell
    let title;

    if (selectionType !== 'radio') {
      let customizeSelections: VNode | null = null;

      if (mergedSelections.value) {
        const menu = h(
          CdsMenu,
          {
            openOnHover: true
          },
          {
            activator: ({ props }: { props: ComponentProps<typeof CdsMenu> }) =>
              h(CdsFunctionalButton, {
                icon: 'chevron-down',
                size: 'sm',
                class: 'cds-table__selection--extra-btn',
                ...props
              }),
            default: () =>
              h(
                CdsList,
                {},
                {
                  default: () =>
                    mergedSelections.value &&
                    mergedSelections.value.map((selection, index) => {
                      const { key, text, onSelect: onSelectionClick } = selection;

                      return h(
                        CdsListItem,
                        { key: key || index, value: key || index, onClick: () => onSelectionClick?.(recordKeys) },
                        {
                          default: () => text
                        }
                      );
                    })
                }
              )
          }
        );
        customizeSelections = h('div', { class: 'cds-table__selection--extra' }, menu);
      }

      const allDisabledData = flattedData.value
        .map((record, index) => {
          const key = getRowKey.value(record, index);
          const checkboxProps = checkboxPropsMap.value.get(key) || {};
          return { checked: keySet.has(key), ...checkboxProps };
        })
        .filter(({ disabled }) => disabled);

      const allDisabled = !!allDisabledData.length && allDisabledData.length === flattedDataLength.value;
      const allDisabledAndChecked = allDisabled && allDisabledData.every(({ modelValue }) => modelValue);
      const allDisabledSomeChecked = allDisabled && allDisabledData.some(({ modelValue }) => modelValue);

      title =
        !hideSelectAll &&
        h('div', { class: 'cds-table__selection' }, [
          h(CdsCheckbox, {
            modelValue: !allDisabled ? !!flattedDataLength.value && checkedCurrentAll : allDisabledAndChecked,
            indeterminate: !allDisabled
              ? !checkedCurrentAll && checkedCurrentSome
              : !allDisabledAndChecked && allDisabledSomeChecked,
            disabled: flattedDataLength.value === 0 || allDisabled,
            hideMessages: true,
            onChange: onSelectAllChange
          }),
          customizeSelections
        ]);
    }

    // Body Cell
    let renderCell: ({ record, index }: { record: RecordType; index?: number }) => { node: VNode; checked: boolean };

    if (selectionType === 'radio') {
      renderCell = ({ record, index }) => {
        const key = getRowKey.value(record, index);
        const checked = keySet.has(key);
        const checkboxProps = checkboxPropsMap.value.get(key);

        return {
          node: h(CdsRadio, {
            ...checkboxProps,
            checked,
            hideMessages: true,
            onClick: (event: MouseEvent) => event.stopPropagation(),
            onChange: (event: MouseEvent | KeyboardEvent) => {
              if (!keySet.has(key)) {
                triggerSingleSelection(key, true, [key], event);
              }
            }
          }),
          checked
        };
      };
    } else {
      renderCell = ({ record, index }) => {
        const key = getRowKey.value(record, index);
        const checked = keySet.has(key);
        const indeterminate = resultHalfSelectedKeySet.value.has(key);
        const checkboxProps = checkboxPropsMap.value.get(key);
        let mergedIndeterminate: boolean;

        if (expandType.value === 'nest') {
          mergedIndeterminate = indeterminate;
          if (typeof checkboxProps?.indeterminate === 'boolean') {
            warn(
              'CdsTable',
              'set `indeterminate` using `rowSelection.getCheckboxProps` is no allowed with tree structure data'
            );
          }
        } else {
          mergedIndeterminate = checkboxProps?.indeterminate ?? indeterminate;
        }

        // Record checked
        return {
          node: h(CdsCheckbox, {
            ...checkboxProps,
            modelValue: checked,
            indeterminate: mergedIndeterminate,
            hideMessages: true,
            onClick: (event: MouseEvent) => event.stopPropagation(),
            onChange: (event: MouseEvent | KeyboardEvent) => {
              const { shiftKey } = event;

              let startIndex = -1;
              let endIndex = -1;

              // Get range of this
              if (shiftKey && checkStrictly) {
                const pointKeys = new Set([lastSelectedKey.value, key]);

                recordKeys.some((recordKey, recordIndex) => {
                  if (pointKeys.has(recordKey)) {
                    if (startIndex === -1) {
                      startIndex = recordIndex;
                    } else {
                      endIndex = recordIndex;
                      return true;
                    }
                  }

                  return false;
                });
              }

              if (endIndex !== -1 && startIndex !== endIndex && checkStrictly) {
                // Batch update selections
                const rangeKeys = recordKeys.slice(startIndex, endIndex + 1);
                const changedKeys: NTable.TKey[] = [];

                if (checked) {
                  rangeKeys.forEach((recordKey) => {
                    if (keySet.has(recordKey)) {
                      changedKeys.push(recordKey);
                      keySet.delete(recordKey);
                    }
                  });
                } else {
                  rangeKeys.forEach((recordKey) => {
                    if (!keySet.has(recordKey)) {
                      changedKeys.push(recordKey);
                      keySet.add(recordKey);
                    }
                  });
                }

                const keys = Array.from(keySet);

                onSelectMultiple?.(
                  !checked,
                  keys.map((recordKey) => getRecordByKey(recordKey)),
                  changedKeys.map((recordKey) => getRecordByKey(recordKey))
                );

                setSelectedKeys(keys);
              } else {
                // Singe record selected
                const originalCheckedKeys = resultSelectedKeys.value;

                if (checkStrictly) {
                  const checkedKeys = checked
                    ? removeItemFromArray(originalCheckedKeys, key)
                    : addItemToArray(originalCheckedKeys, key);

                  triggerSingleSelection(key, !checked, checkedKeys, event);
                } else {
                  // Always fill first
                  const result = conductCheck(
                    [...originalCheckedKeys, key],
                    true,
                    keyEntities.value,
                    maxLevel.value,
                    levelEntities.value!,
                    isCheckboxDisabled as any,
                    preserveSelectedRowKeys
                  );
                  const { checkedKeys, halfCheckedKeys } = result;

                  let nextCheckedKeys = checkedKeys;

                  // If removed, check conduct again to correction
                  if (checked) {
                    const tempKeySet = new Set(checkedKeys);
                    tempKeySet.delete(key);

                    nextCheckedKeys = conductCheck(
                      Array.from(tempKeySet),
                      { checked: false, halfCheckedKeys },
                      keyEntities.value,
                      maxLevel.value,
                      levelEntities.value!,
                      isCheckboxDisabled as any,
                      preserveSelectedRowKeys
                    ).checkedKeys;
                  }

                  triggerSingleSelection(key, !checked, nextCheckedKeys, event);
                }
              }

              setLastSelectedKey(key);
            }
          }),
          checked
        };
      };
    }

    const renderSelectionCell: NTable.NColumn.IColumn<RecordType>['customRender'] = ({ record, index }) => {
      const { node, checked } = renderCell({ record, index });

      if (customizeRenderCell) {
        return customizeRenderCell(checked, record, index, node);
      }

      return node;
    };
    // ===================== Render =====================

    // Insert selection column if not exist
    if (!cloneColumns.includes(SELECTION_COLUMN)) {
      // Always after expand icon
      if (cloneColumns.findIndex((col: any) => col[INTERNAL_COL_DEFINE]?.columnType === 'EXPAND_COLUMN') === 0) {
        const [expandColumn, ...restColumn] = cloneColumns;
        cloneColumns = [expandColumn, SELECTION_COLUMN, ...restColumn];
      } else {
        // Normal insert at first column
        cloneColumns = [SELECTION_COLUMN, ...cloneColumns];
      }
    }

    // Deduplicate selection column
    const selectionColumnIndex = cloneColumns.indexOf(SELECTION_COLUMN);
    if (cloneColumns.filter((col) => col === SELECTION_COLUMN).length > 1) {
      warn('CdsTable', 'Multiple `SELECTION_COLUMN` exist in `columns`.');
    }
    cloneColumns = cloneColumns.filter(
      (column, index) => column !== SELECTION_COLUMN || index === selectionColumnIndex
    );

    // Fixed column logic
    const prevCol: NTable.NColumn.IColumn<RecordType> & Record<string, any> = cloneColumns[selectionColumnIndex - 1];
    const nextCol: NTable.NColumn.IColumn<RecordType> & Record<string, any> = cloneColumns[selectionColumnIndex + 1];

    let mergedFixed: NTable.TFixed | undefined = fixed;

    if (mergedFixed === undefined) {
      if (nextCol?.fixed !== undefined) {
        mergedFixed = nextCol.fixed;
      } else if (prevCol?.fixed !== undefined) {
        mergedFixed = prevCol.fixed;
      }
    }

    if (
      mergedFixed &&
      prevCol &&
      prevCol[INTERNAL_COL_DEFINE]?.columnType === 'EXPAND_COLUMN' &&
      prevCol.fixed === undefined
    ) {
      prevCol.fixed = mergedFixed;
    }

    // Replace with real selection column
    const selectionColumn = {
      fixed: mergedFixed,
      width: selectionColWidth,
      class: 'cds-table__selection-column',
      title: mergedRowSelection.value.columnTitle || title,
      customRender: renderSelectionCell,
      [INTERNAL_COL_DEFINE]: {
        class: 'cds-table__selection-col'
      }
    };

    return cloneColumns.map((col) => (col === SELECTION_COLUMN ? selectionColumn : col));
  };
  // ====================== Columns ======================

  return { transformColumns, selectedKeySet: resultSelectedKeySet };
}

function flattenData<RecordType>(data: RecordType[] | undefined, childrenColumnName: string): RecordType[] {
  let list: RecordType[] = [];

  (data || []).forEach((record) => {
    list.push(record);

    if (record && typeof record === 'object' && childrenColumnName in record) {
      list = [...list, ...flattenData<RecordType>((record as any)[childrenColumnName], childrenColumnName)];
    }
  });

  return list;
}
